import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {AutocompleteSet} from '../../../bfa-api/models/autocomplete-set';
import {ButtonComponent} from '../../buttons/button/button.component';
import {AppButtonTypeEnum} from '../../../enums/button/app-button-type.enum';
import {ButtonColorEnum} from '../../../enums/button/button-color.enum';
import {ButtonTypeEnum} from '../../../enums/button/button-type.enum';
import {AppDialogModal} from '../../../models/modal/dialog/app-dialog-modal.model';
import {APP_MODAL_REF} from '../../../constants/modal/app-modal-ref.constant';
import {ModalService} from '../../../services/modal/modal.service';
import {AppTagAutocompleteDialogData} from '../../../types/modal/dialog/app-tag-autocomplete-dialog-data.type';
import {TagAutocompleteMatchTable} from '../../../models/tag/tag-autocomplete-match-table/tag-autocomplete-match-table.model';
import {TagAutocompleteMatchTableColumn} from '../../../models/tag/tag-autocomplete-match-table/tag-autocomplete-match-table-column.model';
import {TagAutocompleteMatchTableDisplayRow} from '../../../models/tag/tag-autocomplete-match-table/tag-autocomplete-match-table-display-row.model';
import {TagType} from '../../../bfa-api/models/tag-type';
import {TagDateDisplayPipe} from '../../../pipes/tag-date-display/tag-date-display.pipe';
import {TagNumberDisplayPipe} from '../../../pipes/tag-number-display/tag-number-display.pipe';
import {TextInputComponent} from '../../form-elements/text-input/text-input.component';
import {IconComponent} from '../../icon/icon.component';
import {ICONS} from '../../../constants/icons/icons.constants';
import {AppIconSizeEnum} from '../../../enums/app-icon-size.enum';
import {ContentPrefixDirective} from '../../../directives/content-prefix.directive';

@Component({
    selector: 'app-tag-autocomplete-dialog',
    templateUrl: './tag-autocomplete-dialog.component.html',
    styleUrl: './tag-autocomplete-dialog.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        ContentPrefixDirective,
        FormsModule,
        IconComponent,
        TextInputComponent,
        TranslatePipe
    ],
    providers: [
        TagDateDisplayPipe,
        TagNumberDisplayPipe
    ]
})
export class TagAutocompleteDialogComponent {
    public readonly modalData: AppTagAutocompleteDialogData;
    protected readonly matchTable: TagAutocompleteMatchTable;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly AppIconSizeEnum = AppIconSizeEnum;
    protected readonly ButtonColorEnum = ButtonColorEnum;
    protected readonly ButtonTypeEnum = ButtonTypeEnum;
    protected readonly ICONS = ICONS;
    protected readonly qaTag: string;
    protected readonly filterText: WritableSignal<string> = signal('');
    protected readonly visibleRows: Signal<Array<TagAutocompleteMatchTableDisplayRow>> = computed(() => this.getVisibleRows(this.filterText()));
    protected selectedRowIndex?: number;
    private readonly displayRows: Array<TagAutocompleteMatchTableDisplayRow>;
    private readonly modalService = inject(ModalService);
    private readonly modalRef = inject<AppDialogModal<TagAutocompleteDialogComponent, AppTagAutocompleteDialogData, AutocompleteSet>>(APP_MODAL_REF);
    private readonly tagDateDisplayPipe = inject(TagDateDisplayPipe);
    private readonly tagNumberDisplayPipe = inject(TagNumberDisplayPipe);

    public constructor() {
        this.modalData = this.modalRef.modalData;
        this.matchTable = this.modalData.matchTable;
        this.qaTag = this.modalRef.qaTag;
        this.displayRows = this.matchTable.rows.map((row, rowIndex) => {
            const displayValues = this.matchTable.columns.map((column, columnIndex) => this.getDisplayValue(column, row.values[columnIndex] ?? ''));

            return {
                rowIndex,
                displayValues,
                searchableText: [
                    ...displayValues,
                    ...row.values,
                ].join('\n')
                    .toLowerCase(),
            };
        });
    }

    protected selectRow(rowIndex: number): void {
        this.selectedRowIndex = rowIndex;
    }

    protected onFilterTextChange(filterText: string | null | undefined): void {
        this.filterText.set(filterText ?? '');

        const selectedRowIndex = this.selectedRowIndex;
        if (selectedRowIndex !== undefined && !this.visibleRows()
            .some(displayRow => displayRow.rowIndex === selectedRowIndex)) {
            this.selectedRowIndex = undefined;
        }
    }

    protected confirm(): void {
        if (this.selectedRowIndex === undefined) {
            return;
        }

        const selectedRow = this.matchTable.rows.at(this.selectedRowIndex);
        if (!selectedRow) {
            return;
        }

        this.modalService.closeModal(this.modalRef, selectedRow.set);
    }

    protected cancel(): void {
        this.modalService.closeModal(this.modalRef);
    }

    protected getMatchRadioId(rowIndex: number): string {
        return `tag-autocomplete-match-${rowIndex}`;
    }

    protected getColumnHeaderId(columnIndex: number): string {
        return `tag-autocomplete-column-${columnIndex}`;
    }

    protected getMatchValueId(rowIndex: number, columnIndex: number): string {
        return `tag-autocomplete-match-${rowIndex}-value-${columnIndex}`;
    }

    protected getMatchLabelledBy(rowIndex: number): string {
        const values = this.matchTable.rows.at(rowIndex)?.values ?? [];
        const valueLabelIds = this.matchTable.columns.flatMap((column, columnIndex) => values[columnIndex]
            ? [
                this.getColumnHeaderId(columnIndex),
                this.getMatchValueId(rowIndex, columnIndex),
            ]
            : []);

        return [
            this.getMatchRadioId(rowIndex),
            ...valueLabelIds,
        ].join(' ');
    }

    private getVisibleRows(filterText: string): Array<TagAutocompleteMatchTableDisplayRow> {
        const filterWords = filterText.toLowerCase()
            .split(/\s+/)
            .filter(filterWord => !!filterWord);

        if (filterWords.length === 0) {
            return this.displayRows;
        }

        return this.displayRows.filter(displayRow => filterWords.every(filterWord => displayRow.searchableText.includes(filterWord)));
    }

    private getDisplayValue(column: TagAutocompleteMatchTableColumn, value: string): string {
        if (column.tagType === TagType.Date) {
            return this.tagDateDisplayPipe.transform({
                value,
                format: column.tagValueFormat,
                isDefaultTagSource: column.isDefaultTagSource,
                isUsingBrowserLocale: true,
            });
        }

        if (column.tagType === TagType.Number) {
            return this.tagNumberDisplayPipe.transform(value, column.tagValueDisplayType, column.tagValueFormat) ?? '';
        }

        return value;
    }
}